import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ApiError, cancelVideoRequest, deleteMyResult, deleteTheme, fetchDeletionPreview, fetchMyVideoRequests, type VideoRequestView, fetchMyResults, fetchMyThemes, requestThemeImages,
} from '../api/client';
import type { MyResult, MyTheme } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { providerLabelOf } from '../auth/config';
import { QuotaNote, imageStatusText } from '../components/ImageStatus';
import { Portrait } from '../components/Portrait';

/**
 * 自分のページ（`/me`、doc/AUTH.md §11）。ログインした人のための管理画面。
 *
 *   受けた診断   ログイン中に受けた結果の履歴
 *   作った診断   自分が作ったテーマ。絵を描く・消す
 *   動画         相性ドラマの動画のリクエストと、できた動画（doc/VIDEO.md §8）
 *   アカウント   表示名、ログイン方法、メールアドレスの確認、ログアウト、アカウントの削除（doc/AUTH.md §18）
 */
type Tab = 'results' | 'themes' | 'videos' | 'account';

const TABS: readonly { readonly id: Tab; readonly label: string }[] = [
  { id: 'results', label: '受けた診断' },
  { id: 'themes', label: '作った診断' },
  { id: 'videos', label: '動画' },
  { id: 'account', label: 'アカウント' },
];

const fail = (cause: unknown, fallback: string) => (cause instanceof ApiError ? cause.message : fallback);

export function MyPage() {
  const { ready, user, enabled, busy, requestSignIn, quota } = useAuth();
  // `?tab=videos` のように、開くタブをリンクで指定できる
  const [params] = useSearchParams();
  const initialTab = TABS.find((t) => t.id === params.get('tab'))?.id ?? 'results';
  const [tab, setTab] = useState<Tab>(initialTab);
  // 自分のページにいるときにタブ付きのリンクを押しても切り替わるように
  const tabParam = params.get('tab');
  useEffect(() => {
    const next = TABS.find((t) => t.id === tabParam)?.id;
    if (next) setTab(next);
  }, [tabParam]);
  const [error, setError] = useState('');

  if (!ready) return <p className="page__notice">読み込んでいます…</p>;

  if (!user) {
    return (
      <main className="page">
        <h1 className="page__title">自分のページ</h1>
        <p className="page__lead">
          ログインすると、受けた診断の履歴、自分が作った診断の管理ができます。診断を受けるだけならログインは要りません。
        </p>
        <div className="page__actions">
          <button className="button button--primary" type="button" onClick={() => void requestSignIn()} disabled={busy || !enabled}>
            {enabled ? (busy ? 'ログイン中…' : 'ログインする') : 'ログイン準備中'}
          </button>
          <Link className="button button--ghost" to="/">トップへ戻る</Link>
        </div>
        <p className="page__footnote">はじめての方は <Link to="/help">ヘルプ</Link> もどうぞ。</p>
      </main>
    );
  }

  return (
    <main className="page">
      <h1 className="page__title">自分のページ</h1>
      <QuotaNote quota={quota} />
      <VerifyBanner />
      {error && <p className="page__error" role="alert">{error}</p>}

      <div className="admin__tabs" role="tablist">
        {TABS.map((item) => (
          <button key={item.id} role="tab" aria-selected={tab === item.id} type="button"
            className={`chip${tab === item.id ? ' chip--on' : ''}`}
            onClick={() => { setTab(item.id); setError(''); }}>
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'results' && <ResultsTab onError={setError} />}
      {tab === 'themes' && <ThemesTab onError={setError} />}
      {tab === 'videos' && <VideosTab onError={setError} />}
      {tab === 'account' && <AccountTab />}
    </main>
  );
}

/** メールアドレスで登録して、まだ確認が済んでいないときだけ出す帯。 */
function VerifyBanner() {
  const { email, emailVerified, provider, sendVerification, reloadUser } = useAuth();
  const [state, setState] = useState<'idle' | 'sent' | 'error'>('idle');
  if (!email || emailVerified || provider !== 'password') return null;
  return (
    <div className="verify-banner" role="status">
      <p className="verify-banner__text">
        <strong>{email}</strong> の確認がまだ済んでいません。届いたメールのリンクを開いてください（パスワードを忘れたときの再設定に使います）。
      </p>
      <div className="verify-banner__actions">
        <button className="button button--ghost button--small" type="button"
          onClick={() => void sendVerification().then(() => setState('sent')).catch(() => setState('error'))}>
          確認メールを送り直す
        </button>
        <button className="button button--ghost button--small" type="button" onClick={() => void reloadUser()}>
          確認できたので読み込み直す
        </button>
      </div>
      {state === 'sent' && <p className="verify-banner__note">送りました。迷惑メールに入っていないか見てください。</p>}
      {state === 'error' && <p className="verify-banner__note">送れませんでした。しばらく待ってからお試しください。</p>}
    </div>
  );
}

function ResultsTab({ onError }: { readonly onError: (message: string) => void }) {
  const [results, setResults] = useState<readonly MyResult[] | null>(null);
  const [working, setWorking] = useState('');

  useEffect(() => {
    void fetchMyResults()
      .then((r) => setResults(r.results))
      .catch((cause: unknown) => onError(fail(cause, '受けた診断を読み込めませんでした。')));
  }, [onError]);

  const remove = async (result: MyResult) => {
    if (!window.confirm(`「${result.characterName ?? result.typeCode}」（${result.themeTitle}）の結果を消します。回答も一緒に消え、結果ページは誰も開けなくなります。元には戻せません。よろしいですか？`)) return;
    setWorking(result.resultId);
    try {
      await deleteMyResult(result.resultId);
      setResults((current) => current?.filter((r) => r.resultId !== result.resultId) ?? null);
    } catch (cause) {
      onError(fail(cause, '消せませんでした。'));
    } finally {
      setWorking('');
    }
  };

  if (results === null) return <p className="page__notice">読み込んでいます…</p>;
  if (results.length === 0) {
    return (
      <p className="page__lead">
        ログインしてから受けた診断がここに並びます。<Link to="/">トップ</Link>から受けてみてください。ログインせずに受けた分は、結果ページの URL をご自身で控えてください。
      </p>
    );
  }
  return (
    <>
    <ul className="mine">
      {results.map((result) => (
        <li key={result.resultId} className="mine__row">
          <Portrait brief={{ name: result.characterName ?? '?', portrait: result.characterPortrait, emoji: result.characterEmoji }} size={48} className="mine__face" loading="lazy" />
          <div className="mine__text">
            <p className="mine__title"><Link to={result.href}>{result.characterName ?? result.typeCode}</Link></p>
            <p className="mine__meta">
              {result.themeTitle}{result.themeDeleted ? '（削除済み）' : ''} ／ {result.typeCode} ／ {new Date(result.createdAt).toLocaleDateString('ja-JP')}
            </p>
          </div>
          <div className="mine__actions">
            <Link className="button button--ghost button--small" to={result.href}>結果を見る</Link>
            <button className="button button--danger button--small" type="button" disabled={working === result.resultId}
              onClick={() => void remove(result)}>
              消す
            </button>
          </div>
        </li>
      ))}
    </ul>
    <p className="page__footnote">消した結果は、回答ごと消えて元に戻せません。シェアした結果ページの URL も開けなくなります。</p>
    </>
  );
}

function ThemesTab({ onError }: { readonly onError: (message: string) => void }) {
  const { refresh } = useAuth();
  const [themes, setThemes] = useState<readonly MyTheme[] | null>(null);
  const [working, setWorking] = useState('');

  const load = useCallback(async () => {
    try {
      setThemes((await fetchMyThemes()).themes);
    } catch (cause) {
      onError(fail(cause, '読み込めませんでした。'));
    }
  }, [onError]);

  useEffect(() => { void load(); }, [load]);

  const act = async (themeId: string, run: () => Promise<unknown>, message: string) => {
    setWorking(themeId);
    try {
      await run();
      await Promise.all([load(), refresh()]);
    } catch (cause) {
      onError(fail(cause, message));
    } finally {
      setWorking('');
    }
  };

  if (themes === null) return <p className="page__notice">読み込んでいます…</p>;
  if (themes.length === 0) {
    return <p className="page__lead">まだありません。<Link to="/">トップの「自分で作る」</Link>からテーマを書くと、ここに並びます。</p>;
  }

  return (
    <>
      <ul className="mine">
        {themes.map((theme) => {
          const deleted = Boolean(theme.deletedAt);
          const canDraw = !deleted && (theme.images === null || ['login_required', 'quota', 'partial'].includes(theme.images.status));
          return (
            <li key={theme.themeId} className={`mine__row${deleted ? ' mine__row--deleted' : ''}`}>
              <div className="mine__text">
                <p className="mine__title">
                  {deleted ? theme.title : <Link to={theme.href}>{theme.title}</Link>}
                  {deleted && <span className="mine__badge">削除済み</span>}
                </p>
                <p className="mine__meta">
                  {theme.characterCount} タイプ ／ {theme.plays} 人が受けた ／ {new Date(theme.createdAt).toLocaleDateString('ja-JP')}
                </p>
                <p className="mine__meta">{imageStatusText(theme.images)}</p>
              </div>
              <div className="mine__actions">
                {canDraw && (
                  <button className="button button--ghost button--small" type="button" disabled={working === theme.themeId}
                    onClick={() => void act(theme.themeId, () => requestThemeImages(theme.themeId), '絵を描き始められませんでした。')}>
                    絵を描く
                  </button>
                )}
                {!deleted && (
                  <button className="button button--danger button--small" type="button" disabled={working === theme.themeId}
                    onClick={() => {
                      const warn = theme.plays > 0 ? `${theme.plays} 人が受けた診断です。` : '';
                      if (!window.confirm(`「${theme.title}」を消します。${warn}この診断の入口は開けなくなります（すでに出ている結果は残ります）。よろしいですか？`)) return;
                      void act(theme.themeId, () => deleteTheme(theme.themeId), '消せませんでした。');
                    }}>
                    消す
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <p className="page__footnote">消した診断は一覧と入口から消えます。すでに出ている結果ページは残ります。戻したいときは管理者に頼んでください。</p>
    </>
  );
}

const VIDEO_STATUS: Record<string, string> = {
  queued: '作りはじめました', planning: '絵コンテを書いています', generating: '映像を描いています', stitching: '仕上げています', done: 'できました', failed: 'うまく作れませんでした（運営が作り直します）',
};

/** 相性ドラマの動画のリクエストと、その結果（doc/VIDEO.md §8）。作りはじめたものは 20 秒ごとに取り直す。 */
function VideosTab({ onError }: { readonly onError: (message: string) => void }) {
  const [items, setItems] = useState<readonly VideoRequestView[] | null>(null);
  const [limit, setLimit] = useState(2);
  const load = useCallback(async () => {
    try {
      const r = await fetchMyVideoRequests();
      setItems(r.requests);
      setLimit(r.monthlyLimit);
    } catch (cause) {
      onError(fail(cause, '動画のリクエストを読めませんでした。'));
    }
  }, [onError]);
  useEffect(() => { void load(); }, [load]);
  const making = items?.some((r) => r.status === 'accepted' && r.video && r.video.status !== 'done' && r.video.status !== 'failed');
  useEffect(() => {
    if (!making) return undefined;
    const timer = window.setInterval(() => void load(), 20_000);
    return () => window.clearInterval(timer);
  }, [making, load]);

  const cancel = async (id: string) => {
    if (!window.confirm('このリクエストを取り下げますか？')) return;
    try { await cancelVideoRequest(id); await load(); } catch (cause) { onError(fail(cause, '取り下げられませんでした。')); }
  };

  if (!items) return <p className="page__notice">読み込んでいます…</p>;
  return (
    <div className="panel">
      <p className="panel__lead">
        診断の入口で、キャラクター 2 人とどんな場面を見たいかを書いてリクエストできます。
        <strong>動画は 1 本作るのに大きな費用がかかるため、作れるのはリクエストのうちごく一部です。</strong>
        運営が選んで作り、できたらここに届きます。リクエストは 1 人 1 件ずつ、30 日で {limit} 件までです。
      </p>
      {items.length === 0 && <p className="page__notice">まだリクエストはありません。診断の入口の下の「2 人の相性ドラマの動画をリクエストする」から出せます。</p>}
      <ul className="mine">
        {items.map((r) => (
          <li key={r.requestId} className="mine__row">
            <div className="mine__text">
              <p className="mine__title"><strong>{r.characterNames[0]} × {r.characterNames[1]}</strong> </p>
              <p className="mine__meta">{r.themeTitle}／{new Date(r.createdAt).toLocaleDateString('ja-JP')}</p>
              <p className="page__footnote">「{r.prompt}」</p>
              <p>
                {r.status === 'pending' && '運営が確かめています。作れるのはごく一部なので、見送りになることもあります。'}
                {r.status === 'cancelled' && '取り下げました。'}
                {r.status === 'declined' && <>今回は見送りになりました。{r.note && <>運営から：{r.note}</>}</>}
                {r.status === 'accepted' && (r.video ? `${VIDEO_STATUS[r.video.status] ?? r.video.status}${r.video.status !== 'done' && r.video.total ? `（${r.video.done}/${r.video.total}）` : ''}` : '作ることになりました。')}
              </p>
              {r.video?.url && (
                <>
                  <video className="mine__video" src={r.video.url} controls playsInline preload="metadata" />
                  <p><a className="button button--ghost button--small" href={r.video.url} download>ダウンロード</a></p>
                </>
              )}
            </div>
            {r.status === 'pending' && <div className="mine__actions"><button className="button button--ghost button--small" type="button" onClick={() => void cancel(r.requestId)}>取り下げる</button></div>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AccountTab() {
  const { user, email, emailVerified, provider, isAdmin, quota, terms, signOut, busy } = useAuth();
  return (
    <div className="panel">
      <dl className="facts">
        <dt>表示名</dt><dd><DisplayName current={user?.displayName ?? ''} /></dd>
        <dt>メールアドレス</dt><dd>{email ?? '（このログイン方法では取得していません）'}{email && (emailVerified ? '（確認済み）' : '（未確認）')}</dd>
        <dt>ログイン方法</dt><dd>{providerLabelOf(provider)}</dd>
        <dt>権限</dt><dd>{isAdmin ? '管理者' : '一般'}</dd>
        <dt>今日の絵の残り</dt>
        <dd>{quota ? (quota.remaining === null ? (isAdmin ? '上限なし（管理者）' : '上限なし') : quota.limit === 0 ? 'いまは止めています' : `あと ${quota.remaining} 回 / 1 日 ${quota.limit} 回`) : '—'}</dd>
        <dt>規約への同意</dt>
        <dd>
          {terms?.agreed
            ? `${terms.agreed.agreedAt.slice(0, 10)} に版 ${terms.agreed.version} へ同意${terms.current ? '' : '（新しい版が出ています）'}`
            : 'まだ同意していません'}
        </dd>
      </dl>
      <p className="panel__lead">
        ログインについて保存しているのは利用者 ID と、絵を描いた回数、権限、同意した版だけです。氏名は保存していません。メールアドレスを保存するのは、お問い合わせで返信先を書いたときだけです。くわしくは <Link to="/privacy">プライバシーポリシー</Link> と <Link to="/help">ヘルプ</Link> をご覧ください。
      </p>
      <div className="page__actions">
        <Link className="button button--ghost" to="/contact">お問い合わせ</Link>
        <button className="button button--ghost" type="button" onClick={() => void signOut()} disabled={busy}>ログアウト</button>
      </div>
      <DeleteAccount />
    </div>
  );
}

/**
 * 表示名（20 文字まで。空にすると未設定）。Firebase の表示名だけを書き換え、GenDan には保存しない。
 * 見えるのは本人の画面と管理メニューだけ。
 */
function DisplayName({ current }: { readonly current: string }) {
  const { changeDisplayName } = useAuth();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      await changeDisplayName(value);
      setEditing(false);
    } catch {
      setError('変えられませんでした。通信を確かめて、もう一度お試しください。');
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <>
        {current || '（未設定）'}{' '}
        <button className="button button--ghost button--small" type="button" onClick={() => { setValue(current); setError(''); setEditing(true); }}>変える</button>
      </>
    );
  }
  return (
    <form onSubmit={(e) => void submit(e)}>
      <label className="field">
        <span className="field__label">表示名（20 文字まで・空にすると未設定。ほかの利用者には見えません）</span>
        <input className="field__input" value={value} maxLength={20} autoComplete="nickname" autoFocus onChange={(e) => setValue(e.target.value)} />
      </label>
      {error && <p className="modal__warn" role="alert">{error}</p>}
      <div className="page__actions">
        <button className="button button--primary button--small" type="submit" disabled={busy || value.trim() === current}>{busy ? '保存しています…' : '保存'}</button>
        <button className="button button--ghost button--small" type="button" disabled={busy} onClick={() => setEditing(false)}>やめる</button>
      </div>
    </form>
  );
}

type DeletionPreview = Awaited<ReturnType<typeof fetchDeletionPreview>>;

/**
 * アカウントの削除（doc/AUTH.md §18）。消えるものを数えて見せ、「削除」と打ってもらい、本人確認で入り直してから消す。
 */
function DeleteAccount() {
  const { deleteAccount, loginMethod } = useAuth();
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<DeletionPreview | null>(null);
  const [method, setMethod] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const start = async () => {
    setOpen(true); setTyped(''); setPassword(''); setError(''); setPreview(null);
    try {
      const [p, m] = await Promise.all([fetchDeletionPreview(), loginMethod()]);
      setPreview(p); setMethod(m);
    } catch (e) {
      setError(e instanceof Error ? e.message : '読み込めませんでした。');
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      await deleteAccount(method === 'password' ? password : undefined);
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : '削除できませんでした。もう一度お試しください。');
    } finally {
      setBusy(false);
    }
  };

  if (done) return <p className="panel__lead" role="status">アカウントを削除しました。ご利用ありがとうございました。</p>;

  return (
    <section className="danger-zone" aria-labelledby="delete-account-title">
      <h3 id="delete-account-title">アカウントを削除する</h3>
      <p className="panel__lead">受けた診断の結果・問い合わせと、このアカウントを消します。取り消せません。</p>
      <button className="button button--danger" type="button" onClick={() => void start()}>アカウントを削除する…</button>
      {open && (
        <div className="modal" role="dialog" aria-modal="true" aria-labelledby="delete-title" onClick={(e) => { if (e.target === e.currentTarget && !busy) setOpen(false); }}>
          <form className="modal__panel" onSubmit={(e) => void submit(e)}>
            <h2 id="delete-title" className="modal__title">アカウントを削除する</h2>
            {!preview && !error && <p className="modal__lead">消えるものを確かめています…</p>}
            {preview && (
              <>
                <ul className="modal__lead">
                  <li>受けた診断の結果 {preview.results} 件（回答ごと消え、結果ページも開けなくなります）</li>
                  <li>作った診断 {preview.themes} 件（新しく受けられなくなります。ほかの人がすでに受けた結果ページは残ります）</li>
                  <li>送った問い合わせと、ログインのアカウント</li>
                </ul>
                <label className="field">
                  <span className="field__label">確かめるため「削除」と入れてください</span>
                  <input className="field__input" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
                </label>
                {method === 'password'
                  ? (
                    <label className="field">
                      <span className="field__label">本人確認のため、パスワードを入れてください</span>
                      <input className="field__input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
                    </label>
                  )
                  : <p className="modal__lead">削除を押すと、本人確認のためにもう一度ログインの画面が開きます。</p>}
              </>
            )}
            {error && <p className="modal__warn" role="alert">{error}</p>}
            <div className="page__actions">
              <button className="button button--danger" type="submit" disabled={busy || !preview || typed.trim() !== '削除' || (method === 'password' && !password)}>
                {busy ? '削除しています…' : '削除する'}
              </button>
              <button className="button button--ghost" type="button" disabled={busy} onClick={() => setOpen(false)}>やめる</button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
